import app from '../data/app.svelte.js';
import { currentSession, currentSessionId } from '../data/session.svelte.js';
import { goto as routerGoto, replace as routerReplace } from '@mateothegreat/svelte5-router';
import { cancelPendingRetries } from './api.svelte.js';

/**
 * Navigation helper for appointment booking flow
 * Handles dynamic step ordering based on salon configuration
 */

/**
 * Build full path with client/salon/sessionId prefix
 * If path starts with "/", use it as-is (absolute)
 * Otherwise, prefix with /{client}/{salon}/{sessionId}/
 * @param {string} path - Relative or absolute path
 * @returns {string} Full path
 */
function buildPath(path) {
    if (path.startsWith('/')) {
        return path;
    }
    return `/${currentSession.client}/${currentSession.salon}/${currentSessionId}/${path}`;
}

/**
 * Get the complete flow array based on salon configuration
 * @returns {string[]} Array of step names in order
 */
function getFlowSteps() {
    const steps = [];

    // Google Reserve has a fixed flow
    if (currentSession?.origin === 'google-reserve') {
        steps.push('treatment', 'employee', 'schedule', 'customer', 'validate?');

        // Add emergency message if enabled
        if (app.settings.emergency.enabled) {
            steps.push('message');

            // Stop here if blocked
            if (app.settings.emergency.blocked) {
                return steps;
            }
        }

        // Proceed to confirm/processing
        steps.push('confirm', 'processing');

        return steps;
    }

    // Standard flow
    if (app.settings.emergency.enabled) {
        steps.push('message');

        if (app.settings.emergency.blocked) {
            return steps;
        }
    }

    steps.push('welcome');

    if (currentSession.validate === 'early') {
        steps.push('validate?');
    }

    if (app.settings.stepsOrder === 'treatment,employee') {
        steps.push('treatment', 'employee');
    } else {
        steps.push('employee', 'treatment');
    }

    steps.push('schedule');

    if (currentSession.validate === 'late') {
        steps.push('customer', 'validate?');
    }

    steps.push('confirm', 'processing');

    return steps;
}

/**
 * Get the first step in the flow
 * @returns {string} First step name
 */
export function first() {
    const steps = getFlowSteps();
    return steps[0];
}

/**
 * Get the next step in the flow
 * @param {string} currentStep - Current step name (without '?' postfix)
 * @returns {string|null} Next step name or null if at end
 */
export function next(currentStep) {
    const steps = getFlowSteps();

    // Find the current step in the flow (comparing without optional markers)
    const currentIndex = steps.findIndex(step => step.replace(/\?$/, '') === currentStep);

    if (currentIndex === -1) {
        console.warn(`Current step "${currentStep}" not found in flow`);
        return null;
    }

    if (currentIndex >= steps.length - 1) {
        return null;
    }

    // Find next non-optional step
    for (let i = currentIndex + 1; i < steps.length; i++) {
        if (!steps[i].endsWith('?')) {
            return steps[i];
        }
    }

    // If all remaining steps are optional, return the last one
    return steps[steps.length - 1].replace(/\?$/, '');
}

/**
 * Get the previous step in the flow
 * @param {string} currentStep - Current step name (without '?' postfix)
 * @returns {string|null} Previous step name or null if at beginning
 */
export function previous(currentStep) {
    const steps = getFlowSteps();

    // Find the current step in the flow (comparing without optional markers)
    const currentIndex = steps.findIndex(step => step.replace(/\?$/, '') === currentStep);

    if (currentIndex === -1) {
        console.warn(`Current step "${currentStep}" not found in flow`);
        return null;
    }

    if (currentIndex <= 0) {
        return null;
    }

    // Find previous non-optional step
    for (let i = currentIndex - 1; i >= 0; i--) {
        if (!steps[i].endsWith('?')) {
            return steps[i];
        }
    }

    // If all previous steps are optional, return the first one
    return steps[0].replace(/\?$/, '');
}

/**
 * Navigate to a path and set the direction
 * @param {string} path - Path to navigate to (relative or absolute)
 * @param {string} direction - Direction ('left' or 'right')
 */
export function goto(path, direction) {
    cancelPendingRetries();
    app.state.direction = direction;

    setTimeout(() => {
        window.scrollTo(0, 0);
    }, 50);

    routerGoto(buildPath(path));
}

/**
 * Replace current path and set the direction
 * @param {string} path - Path to replace with (relative or absolute)
 * @param {string} direction - Direction ('left' or 'right')
 */
export function replace(path, direction) {
    cancelPendingRetries();
    app.state.direction = direction;

    setTimeout(() => {
        window.scrollTo(0, 0);
    }, 50);

    routerReplace(buildPath(path));
}

/**
 * Check if all preceding steps in the flow have their required data.
 * Returns the first incomplete step name, or null if all complete.
 * @param {string} currentStep - The step the user is trying to access
 * @returns {string|null} First incomplete step or null
 */
export function getFirstIncompleteStep(currentStep) {
    const steps = getFlowSteps();
    const currentIndex = steps.findIndex(step => step.replace(/\?$/, '') === currentStep);

    if (currentIndex <= 0) {
        return null;
    }

    for (let i = 0; i < currentIndex; i++) {
        const step = steps[i].replace(/\?$/, '');

        if (step === 'treatment' && !currentSession.treatment) return step;
        if (step === 'employee' && !currentSession.employee) return step;
        if (step === 'schedule' && (!currentSession.date || !currentSession.time)) return step;
        if (step === 'customer' && !currentSession.customer?.firstname && !currentSession.pendingCustomer?.firstname) return step;
    }

    return null;
}

/**
 * The booking step to resume at (e.g. when leaving the inline AI chat): the first
 * step in the flow still missing its data, or 'confirm' when everything is set.
 * @returns {string} Step name
 */
export function resumeStep() {
    const steps = getFlowSteps().map(step => step.replace(/\?$/, ''));

    for (const step of steps) {
        if (step === 'treatment' && !currentSession.treatment) return step;
        if (step === 'employee' && !currentSession.employee) return step;
        if (step === 'schedule' && (!currentSession.date || !currentSession.time)) return step;
        if (step === 'customer' && !currentSession.customer?.firstname && !currentSession.pendingCustomer?.firstname) return step;
    }

    return 'confirm';
}